import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  ArrowLeft, Settings, Save, Loader2, Check, Eye, EyeOff,
  CreditCard, Building2, Globe, Phone, Mail, MapPin, Receipt
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// ---- Payment Settings Section ----
const PaymentSection = () => {
  const [form, setForm] = useState({
    paypal_me: '', sumup_link: '', sumup_api_key: '', sumup_merchant_code: '', vat_rate: 22
  });
  const [showKey, setShowKey] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  const fetchSettings = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/api/admin/payment-settings`, { withCredentials: true });
      setForm({
        paypal_me: res.data.paypal_me || '',
        sumup_link: res.data.sumup_link || '',
        sumup_api_key: res.data.sumup_api_key || '',
        sumup_merchant_code: res.data.sumup_merchant_code || '',
        vat_rate: res.data.vat_rate ?? 22,
      });
    } catch (e) { /* silenced */ }
  }, []);

  useEffect(() => { fetchSettings(); }, [fetchSettings]);

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(`${API_URL}/api/admin/payment-settings`, form, { withCredentials: true });
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
    } catch (e) { alert('Errore salvataggio'); }
    finally { setSaving(false); }
  };

  return (
    <Card className="border-[#E4E4E7] shadow-sm" data-testid="settings-payment">
      <CardHeader className="pb-3">
        <CardTitle className="text-base font-bold text-[#09090B] flex items-center gap-2">
          <CreditCard className="w-5 h-5 text-[#0055FF]" />
          Pagamenti e Fatturazione
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-5">
        {/* IVA */}
        <div className="p-3 bg-[#F4F4F5] rounded-lg">
          <Label className="text-sm font-semibold flex items-center gap-1">
            <Receipt className="w-4 h-4 text-[#8B5CF6]" /> Aliquota IVA (%)
          </Label>
          <Input type="number" min="0" max="100" step="1" value={form.vat_rate}
            onChange={(e) => setForm(f => ({ ...f, vat_rate: parseFloat(e.target.value) || 0 }))}
            className="mt-1 max-w-[120px]" data-testid="vat-rate-input" />
          <p className="text-xs text-[#A1A1AA] mt-1">Calcolata automaticamente sull'importo dei buoni di lavoro</p>
        </div>

        {/* PayPal */}
        <div>
          <Label className="text-sm font-semibold flex items-center gap-2">
            <span className="w-5 h-5 rounded bg-[#0070BA] flex items-center justify-center text-white text-[10px] font-bold">P</span>
            PayPal.me
          </Label>
          <Input value={form.paypal_me} onChange={(e) => setForm(f => ({ ...f, paypal_me: e.target.value }))}
            placeholder="es. paypal.me/esaimpianti" className="mt-1" data-testid="paypal-input" />
        </div>

        {/* SumUp Section */}
        <div className="border border-[#E4E4E7] rounded-lg p-4 space-y-3">
          <p className="text-sm font-semibold flex items-center gap-2">
            <span className="w-5 h-5 rounded bg-[#1A1A2E] flex items-center justify-center text-white text-[10px] font-bold">S</span>
            SumUp — Pagamenti e Ricevute Fiscali
          </p>
          <div>
            <Label className="text-xs font-medium text-[#71717A]">Link Pagamento (opzionale, fallback)</Label>
            <Input value={form.sumup_link} onChange={(e) => setForm(f => ({ ...f, sumup_link: e.target.value }))}
              placeholder="es. https://pay.sumup.com/b2c/..." className="mt-1" data-testid="sumup-link-input" />
          </div>
          <div>
            <Label className="text-xs font-medium text-[#71717A]">API Key</Label>
            <div className="flex gap-2 mt-1">
              <Input type={showKey ? 'text' : 'password'} value={form.sumup_api_key}
                onChange={(e) => setForm(f => ({ ...f, sumup_api_key: e.target.value }))}
                placeholder="sup_sk_..." className="flex-1" data-testid="sumup-api-key-input" />
              <Button type="button" variant="outline" size="sm" onClick={() => setShowKey(!showKey)}
                data-testid="toggle-key-visibility">
                {showKey ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </Button>
            </div>
          </div>
          <div>
            <Label className="text-xs font-medium text-[#71717A]">Merchant Code</Label>
            <Input value={form.sumup_merchant_code}
              onChange={(e) => setForm(f => ({ ...f, sumup_merchant_code: e.target.value }))}
              placeholder="es. MCXXXXXX" className="mt-1" data-testid="sumup-merchant-input" />
          </div>
          <p className="text-xs text-[#A1A1AA]">
            Con API Key e Merchant Code attivi, il sistema crea checkout automatici e permette l'emissione di ricevute fiscali.
            Ottieni le credenziali su{' '}
            <a href="https://me.sumup.com/settings/developer" target="_blank" rel="noreferrer" className="text-[#0055FF] underline">
              me.sumup.com/settings/developer
            </a>
          </p>
        </div>

        <Button onClick={handleSave} disabled={saving}
          className="w-full bg-[#0055FF] hover:bg-[#0044CC] text-white font-semibold"
          data-testid="save-payment-settings-btn">
          {saving ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : saved ? <Check className="w-4 h-4 mr-1" /> : <Save className="w-4 h-4 mr-1" />}
          {saving ? 'Salvataggio...' : saved ? 'Salvato!' : 'Salva Impostazioni Pagamento'}
        </Button>
      </CardContent>
    </Card>
  );
};

// ---- Company Info Section ----
const CompanySection = () => {
  return (
    <Card className="border-[#E4E4E7] shadow-sm" data-testid="settings-company">
      <CardHeader className="pb-3">
        <CardTitle className="text-base font-bold text-[#09090B] flex items-center gap-2">
          <Building2 className="w-5 h-5 text-[#0055FF]" />
          Dati Aziendali
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="space-y-2.5">
          <div className="flex items-start gap-3 p-3 bg-[#F4F4F5] rounded-lg">
            <Building2 className="w-4 h-4 text-[#71717A] mt-0.5 shrink-0" />
            <div>
              <p className="text-sm font-semibold text-[#09090B]">Esa Impianti di Soldani Giulio & C.</p>
              <p className="text-xs text-[#71717A]">Ragione Sociale</p>
            </div>
          </div>
          <div className="flex items-start gap-3 p-3 bg-[#F4F4F5] rounded-lg">
            <MapPin className="w-4 h-4 text-[#71717A] mt-0.5 shrink-0" />
            <div>
              <p className="text-sm text-[#09090B]">Via Sant'Andrea 141, Bisceglie (BT) 76011</p>
              <p className="text-xs text-[#71717A]">Indirizzo</p>
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
            <div className="flex items-start gap-3 p-3 bg-[#F4F4F5] rounded-lg">
              <Phone className="w-4 h-4 text-[#71717A] mt-0.5 shrink-0" />
              <div>
                <p className="text-sm text-[#09090B]">335 786 3535</p>
                <p className="text-xs text-[#71717A]">Telefono / WhatsApp</p>
              </div>
            </div>
            <div className="flex items-start gap-3 p-3 bg-[#F4F4F5] rounded-lg">
              <Mail className="w-4 h-4 text-[#71717A] mt-0.5 shrink-0" />
              <div>
                <p className="text-sm text-[#09090B] break-all">amministrazione@esaimpiantisnc.com</p>
                <p className="text-xs text-[#71717A]">Email</p>
              </div>
            </div>
            <div className="flex items-start gap-3 p-3 bg-[#F4F4F5] rounded-lg">
              <Receipt className="w-4 h-4 text-[#71717A] mt-0.5 shrink-0" />
              <div>
                <p className="text-sm text-[#09090B]">P.IVA 05355910729</p>
                <p className="text-xs text-[#71717A]">Partita IVA</p>
              </div>
            </div>
            <div className="flex items-start gap-3 p-3 bg-[#F4F4F5] rounded-lg">
              <Globe className="w-4 h-4 text-[#71717A] mt-0.5 shrink-0" />
              <div>
                <a href="https://www.esaimpiantisnc.com" target="_blank" rel="noreferrer"
                  className="text-sm text-[#0055FF] underline">www.esaimpiantisnc.com</a>
                <p className="text-xs text-[#71717A]">Sito Web</p>
              </div>
            </div>
          </div>
        </div>
        <p className="text-xs text-[#A1A1AA] pt-1">
          Questi dati appaiono nell'intestazione dei Buoni di Lavoro e nei PDF generati.
        </p>
      </CardContent>
    </Card>
  );
};

// ---- Main Settings Page ----
const SettingsPage = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const isAdmin = user?.role === 'admin';

  if (!isAdmin) {
    return (
      <div className="min-h-screen bg-[#FAFAFA] flex items-center justify-center">
        <p className="text-[#71717A]">Accesso riservato all'amministratore</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FAFAFA]" data-testid="settings-page">
      {/* Header */}
      <header className="bg-white border-b border-[#E4E4E7] sticky top-0 z-30">
        <div className="max-w-3xl mx-auto px-4 py-3 flex items-center justify-between">
          <Button variant="ghost" size="sm" onClick={() => navigate('/dashboard')} data-testid="settings-back-btn">
            <ArrowLeft className="w-4 h-4 mr-1" /> Dashboard
          </Button>
          <h1 className="text-lg font-bold text-[#09090B] flex items-center gap-2">
            <Settings className="w-5 h-5 text-[#0055FF]" /> Impostazioni
          </h1>
          <div className="w-24" />
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-4 py-6 space-y-6">
        <CompanySection />
        <PaymentSection />
      </main>
    </div>
  );
};

export default SettingsPage;
